// Region / group / individual pill pickers shared by the Driving, Resilience and
// Compliance screens (prototype's inline button rows under each drill scope).

import React from 'react';
import { cn } from '@/lib/utils';
import { GROUPS, REGIONS, readiness, wArr, workersInGroup, workersInRegion } from '../engine/data';
import type { Worker } from '../engine/data';
import { useDash } from '../state';
import { Dot, toneText } from '../ui';
import type { Tone } from '../ui';

const idleBtn = 'border-border bg-muted text-muted-foreground hover:bg-accent hover:text-foreground';
const activeBtn = 'border-success/30 bg-success/10 text-success';

/** Region pills. `resetGroup` also jumps to the region's first group (group scope). */
export const RegionPicker: React.FC<{ resetGroup?: boolean; compact?: boolean }> = ({ resetGroup, compact }) => {
  const d = useDash();
  const current = resetGroup && d.activeGroup !== null ? GROUPS[d.activeGroup].region : d.activeRegion;
  return (
    <div className={cn('flex flex-wrap gap-2', compact ? 'mb-2' : 'mb-4')}>
      {Object.entries(REGIONS).map(([rid, r]) => {
        const active = rid === current;
        return (
          <button
            key={rid}
            type='button'
            onClick={() =>
              d.patch(resetGroup ? { activeRegion: rid, activeGroup: r.groups[0] } : { activeRegion: rid })
            }
            className={cn(
              'rounded-full border-2 font-semibold transition-colors',
              compact ? 'px-3 py-1 text-[11px]' : 'px-4 py-1.5 text-[13px]',
              !active && idleBtn,
            )}
            // Region colour comes from data, so it can't be a Tailwind class.
            style={active ? { borderColor: r.color, background: `${r.color}22`, color: r.color } : undefined}
          >
            {r.name}
          </button>
        );
      })}
    </div>
  );
};

export const GroupPicker: React.FC<{ regionId: string; className?: string }> = ({ regionId, className }) => {
  const d = useDash();
  return (
    <div className={cn('mb-4 flex flex-wrap gap-1.5', className)}>
      {REGIONS[regionId].groups.map((gid) => (
        <button
          key={gid}
          type='button'
          onClick={() => d.patch({ activeGroup: gid })}
          className={cn(
            'rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors',
            gid === d.activeGroup ? activeBtn : idleBtn,
          )}
        >
          {GROUPS[gid].name}
        </button>
      ))}
    </div>
  );
};

/** Pool the individual picker draws from: group, else region, else everyone. */
export function individualPool(activeGroup: number | null, activeRegion: string | null): Worker[] {
  return activeGroup !== null ? workersInGroup(activeGroup) : activeRegion ? workersInRegion(activeRegion) : wArr();
}

export const WorkerPicker: React.FC = () => {
  const d = useDash();
  const sorted = [...individualPool(d.activeGroup, d.activeRegion)].sort((a, b) => b.fed - a.fed);
  return (
    <div className='mb-3 flex flex-wrap gap-1.5 border-b border-border pb-3'>
      <span className='mr-1 self-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
        Individual
      </span>
      {sorted.map((p) => {
        const pr = readiness(p);
        const tone: Tone = pr === 2 ? 'bad' : pr === 1 ? 'warn' : 'good';
        return (
          <button
            key={p.init}
            type='button'
            onClick={() => d.patch({ activeWorker: p.init })}
            className={cn(
              'flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
              p.init === d.activeWorker ? activeBtn : idleBtn,
            )}
          >
            <Dot tone={tone} className='h-1.5 w-1.5' />
            {p.init} {p.name.split(' ')[0]}
          </button>
        );
      })}
    </div>
  );
};

/** Small number tile used on the region summary cards. */
export const StatTile: React.FC<{ value: React.ReactNode; label: string; tone?: Tone; alert?: boolean }> = ({
  value,
  label,
  tone,
  alert,
}) => (
  <div
    className={cn(
      'rounded-lg border p-2',
      alert
        ? tone === 'warn'
          ? 'border-warning bg-warning/10'
          : 'border-destructive bg-destructive/10'
        : 'border-transparent bg-muted',
    )}
  >
    <div className={cn('text-lg font-bold tabular-nums', tone ? toneText[tone] : 'text-foreground')}>{value}</div>
    <div className='text-[10px] text-muted-foreground'>{label}</div>
  </div>
);
